import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";

const DISMISS_KEY = "imp.twofaGraceDismissed";

/**
 * Non-blocking nudge shown to an internal user who must enrol 2FA but is still inside their grace
 * window (setupRequired is false, so the app stays usable). Dismissible for the session; once the
 * grace window elapses the RequireAuth gate takes over with the blocking setup screen instead.
 */
export default function TwoFactorGraceBanner() {
  const { state } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  if (state.status !== "authenticated") return null;
  const tf = state.twoFactor;
  // Only nudge when 2FA is owed but not yet mandatory-blocking and not already enrolled.
  if (!tf?.required || tf.enabled || tf.setupRequired || dismissed) return null;

  const daysLeft = tf.graceUntil ? daysUntil(tf.graceUntil) : null;

  function dismiss() {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  }

  return (
    <div style={banner} role="status">
      <span style={{ fontSize: 14 }}>
        <strong>{t("twofa.grace.title", "Set up two-factor authentication")}</strong>{" "}
        {daysLeft != null && daysLeft > 0
          ? t("twofa.grace.daysLeft", "Required for your role — {{count}} day(s) left before it becomes mandatory.", { count: daysLeft })
          : t("twofa.grace.soon", "Required for your role — set it up before it becomes mandatory.")}
      </span>
      <span style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        <button type="button" onClick={() => navigate("/profile")} style={setupBtn}>
          {t("twofa.grace.setUp", "Set up now")}
        </button>
        <button type="button" onClick={dismiss} style={dismissBtn} aria-label={t("common.dismiss", "Dismiss")}>
          ✕
        </button>
      </span>
    </div>
  );
}

/** Whole days from now until the ISO deadline, floored at 0. */
function daysUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

const banner: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
  padding: "10px 16px",
  margin: "0 0 16px",
  background: "#fffbeb",
  border: "1px solid #fde68a",
  borderRadius: 8,
  color: "#92400e",
};
const setupBtn: React.CSSProperties = {
  padding: "6px 12px",
  background: "#0b3a6f",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};
const dismissBtn: React.CSSProperties = {
  padding: "6px 10px",
  background: "transparent",
  color: "#92400e",
  border: "1px solid #fde68a",
  borderRadius: 6,
  fontSize: 13,
  cursor: "pointer",
};
